import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Box, Typography, CircularProgress, Chip } from "@mui/material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { fetchFollowUpMessages } from "../../utils/APIHelper";
import { getContextAwareToken } from "../../utils/storageHelper";
import { useAuth } from "../../context/AuthContext";

dayjs.extend(relativeTime);

interface FollowUpMessage {
  id: string;
  appointment_id: string;
  patient_name: string;
  provider_name: string;
  message: string;
  message_time: string;
  follow_up_count: number;
  cancellation_date: string;
  reason_cancellation: string;
  created_at: string;
}

interface FollowUpMessagesProps {
  patientId: string;
  providerId: string;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export interface FollowUpMessagesRef {
  refresh: () => void;
}

export const FollowUpMessages = forwardRef<
  FollowUpMessagesRef,
  FollowUpMessagesProps
>(
  (
    {
      patientId,
      providerId,
      autoRefresh = true,
      refreshInterval = 30000, // 30 seconds
    },
    ref
  ) => {
    const { user } = useAuth();
    const [visibleMessages, setVisibleMessages] = useState<FollowUpMessage[]>(
      []
    ); // Messages currently visible in UI
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [is404Error, setIs404Error] = useState(false);
    const hasScheduledRef = useRef(false); // Track if we've already scheduled auto-refresh
    const messageDelayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
      null
    );
    const secondMessageScheduledRef = useRef(false); // Track if we've scheduled the second message

    const loadMessages = useCallback(
      async (showLoading = true) => {
        if (showLoading) setLoading(true);
        setError(null);

        try {
          const token = getContextAwareToken(user?.token, user?.role) || "";

          if (!token) {
            setError("Authentication token not found");
            setLoading(false);
            return;
          }

          const response = await fetchFollowUpMessages(
            token,
            patientId,
            providerId,
            true
          );

          // Handle different response structures
          if (response?.success !== false) {
            let allMessages: FollowUpMessage[] = [];

            // Try to extract messages from different possible response structures
            if (response?.data?.threads) {
              // Nested threads structure
              Object.values(response.data.threads).forEach(
                (threadMessages: any) => {
                  if (Array.isArray(threadMessages)) {
                    allMessages.push(...threadMessages);
                  }
                }
              );
            } else if (response?.data && Array.isArray(response.data)) {
              // Direct array in data
              allMessages = response.data;
            } else if (Array.isArray(response)) {
              // Direct array response
              allMessages = response;
            } else if (response?.success && response?.data) {
              // Success wrapper with data object
              if (Array.isArray(response.data)) {
                allMessages = response.data;
              }
            }

            // Sort messages by message_time (newest first)
            if (allMessages.length > 0) {
              allMessages.sort(
                (a, b) =>
                  new Date(b.message_time || b.created_at).getTime() -
                  new Date(a.message_time || a.created_at).getTime()
              );
            }

            setIs404Error(false);

            // Stagger message visibility for better UX
            if (allMessages.length > 0) {
              // Sort by follow_up_count to ensure proper order (1st message first, then 2nd)
              const sortedMessages = [...allMessages].sort(
                (a, b) => a.follow_up_count - b.follow_up_count
              );

              const firstMessage = sortedMessages.find(
                (msg) => msg.follow_up_count === 1
              );
              const secondMessage = sortedMessages.find(
                (msg) => msg.follow_up_count === 2
              );

              // Check if messages are fresh (created within last 2 minutes)
              const now = new Date();
              const firstMessageTime = firstMessage
                ? new Date(firstMessage.created_at || firstMessage.message_time)
                : null;
              const isFreshAppointment = firstMessageTime
                ? now.getTime() - firstMessageTime.getTime() < 120000
                : false; // 2 minutes

              // Check what's currently visible
              setVisibleMessages((prev) => {
                const hasFirstVisible = prev.find(
                  (m) => m.follow_up_count === 1
                );
                const hasSecondVisible = prev.find(
                  (m) => m.follow_up_count === 2
                );

                // If both messages are already visible, just update them with fresh data
                if (
                  hasFirstVisible &&
                  hasSecondVisible &&
                  firstMessage &&
                  secondMessage
                ) {
                  return [firstMessage, secondMessage];
                }

                // If this is initial load (nothing visible yet)
                if (prev.length === 0) {
                  // For old appointments (not fresh), show both messages immediately
                  if (!isFreshAppointment && firstMessage && secondMessage) {
                    return [firstMessage, secondMessage];
                  }

                  // For fresh appointments, show first message and delay second
                  if (firstMessage) {
                    return [firstMessage];
                  }
                }

                // If we have first message visible but not second, and second message exists
                if (
                  hasFirstVisible &&
                  !hasSecondVisible &&
                  secondMessage &&
                  !secondMessageScheduledRef.current
                ) {
                  // For old appointments, show second message immediately
                  if (!isFreshAppointment) {
                    return [firstMessage!, secondMessage];
                  }

                  // For fresh appointments, schedule delay
                  secondMessageScheduledRef.current = true;

                  // Schedule the second message to appear after 60 seconds
                  messageDelayTimeoutRef.current = setTimeout(() => {
                    setVisibleMessages((current) => {
                      const hasSecond = current.find(
                        (m) => m.follow_up_count === 2
                      );
                      if (!hasSecond && secondMessage) {
                        return [...current, secondMessage];
                      }
                      return current;
                    });
                  }, 60000); // 60 seconds delay
                }

                // If second message is already visible, keep it (for refresh scenarios)
                if (hasSecondVisible && firstMessage && secondMessage) {
                  return [firstMessage, secondMessage];
                }

                // Add first message if not already visible (edge case)
                if (firstMessage && !hasFirstVisible) {
                  return [firstMessage];
                }

                return prev; // No changes needed
              });
            } else {
              setVisibleMessages([]);
            }
          } else {
            console.error("[FollowUpMessages] API returned success: false");
            setError("Failed to load follow-up messages");
          }
        } catch (err: any) {
          console.error("[FollowUpMessages] Error loading messages:", err);

          // Check if it's a 404 error (endpoint not found)
          if (
            err?.response?.status === 404 ||
            err?.message?.includes("404") ||
            err?.message?.includes("Not Found")
          ) {
            setIs404Error(true);
            setError(
              "Unable to load follow-up messages. The endpoint may not be properly configured."
            );
          } else if (
            err?.response?.status === 401 ||
            err?.response?.status === 403
          ) {
            setError("Authentication error. Please log in again.");
          } else if (
            err?.response?.status === 500 ||
            err?.message?.includes("500")
          ) {
            // Backend server error - likely no messages generated yet
            const errorDetail = err?.message?.includes(
              "Failed to fetch follow-up messages"
            )
              ? "No follow-up messages have been generated yet. Messages are created 30 seconds after the no-show record is saved. Please wait and the page will automatically refresh."
              : "A server error occurred while loading messages. Please try again in a moment.";
            setError(errorDetail);
            console.error("[FollowUpMessages] Server error details:", err);
            // Don't set is404Error so auto-refresh continues
          } else {
            setError(
              `Error: ${
                err?.message || "An error occurred while loading messages"
              }`
            );
          }
        } finally {
          if (showLoading) setLoading(false);
        }
      },
      [patientId, providerId, user]
    );

    // Expose refresh method to parent component
    useImperativeHandle(
      ref,
      () => ({
        refresh: () => {
          loadMessages(true);
        },
      }),
      [loadMessages]
    );

    useEffect(() => {
      // Only run on initial mount - don't depend on loadMessages to avoid duplicate schedules
      loadMessages();

      // Don't set up auto-refresh if there's a 404 error or autoRefresh is disabled
      if (!autoRefresh || is404Error) {
        return;
      }

      // Prevent scheduling multiple times due to React StrictMode double-mounting
      if (hasScheduledRef.current) {
        return;
      }

      hasScheduledRef.current = true;
      const timeouts: ReturnType<typeof setTimeout>[] = [];

      const scheduleLoad = (delay: number) => {
        const timeoutId = setTimeout(() => {
          loadMessages(false); // Don't show loading spinner on auto-refresh
        }, delay);
        timeouts.push(timeoutId);
      };

      // First follow-up fetch after 30s, second after 60s
      scheduleLoad(refreshInterval);
      scheduleLoad(refreshInterval * 2);

      return () => {
        timeouts.forEach(clearTimeout);

        // Clear message delay timeout
        if (messageDelayTimeoutRef.current) {
          clearTimeout(messageDelayTimeoutRef.current);
          messageDelayTimeoutRef.current = null;
        }

        hasScheduledRef.current = false; // Reset on cleanup
        secondMessageScheduledRef.current = false; // Reset second message schedule flag
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoRefresh, refreshInterval, is404Error]); // Intentionally exclude loadMessages to prevent re-scheduling

    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "40px",
          }}
        >
          <CircularProgress size={30} />
        </Box>
      );
    }

    if (error) {
      return (
        <Box
          sx={{
            padding: "20px",
            backgroundColor: "#FFF5F5",
            borderRadius: "8px",
            border: "1px solid #FEB2B2",
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              color: "#C53030",
              fontFamily: "Quicksand, sans-serif",
            }}
          >
            {error}
          </Typography>
        </Box>
      );
    }

    if (visibleMessages.length === 0) {
      return (
        <Box
          sx={{
            padding: "40px 20px",
            backgroundColor: "#F7FAFC",
            borderRadius: "8px",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              color: "#718096",
              fontFamily: "Quicksand, sans-serif",
              marginBottom: "8px",
            }}
          >
            No follow-up messages available yet.
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              color: "#A0AEC0",
              fontFamily: "Quicksand, sans-serif",
              marginBottom: "4px",
            }}
          >
            Follow-up messages are automatically generated and scheduled 30
            seconds after a no-show record is created.
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              color: "#A0AEC0",
              fontFamily: "Quicksand, sans-serif",
            }}
          >
            {autoRefresh &&
              !is404Error &&
              "This page will automatically check for new messages at 30s and 60s."}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {visibleMessages.map((msg, index) => (
          <Box
            key={msg.id}
            sx={{
              backgroundColor: "#FFFFFF",
              borderRadius: "8px",
              border: "1px solid #E2E8F0",
              padding: "20px",
              position: "relative",
              "&:hover": {
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
              },
            }}
          >
            {/* Header with follow-up count badge */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "12px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: "#2D3748",
                    fontFamily: "Quicksand, sans-serif",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Follow-Up Message
                </Typography>
                <Chip
                  label={`#${msg.follow_up_count}`}
                  size="small"
                  sx={{
                    height: "20px",
                    fontSize: "11px",
                    fontWeight: 600,
                    backgroundColor:
                      msg.follow_up_count === 1 ? "#EDF2F7" : "#FED7D7",
                    color: msg.follow_up_count === 1 ? "#2D3748" : "#C53030",
                    fontFamily: "Quicksand, sans-serif",
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 400,
                  color: "#A0AEC0",
                  fontFamily: "Quicksand, sans-serif",
                }}
              >
                {dayjs(msg.message_time).fromNow()}
              </Typography>
            </Box>

            {/* Message content */}
            <Box
              sx={{
                backgroundColor: "#F7FAFC",
                borderRadius: "6px",
                padding: "16px",
                marginBottom: "12px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 400,
                  color: "#2D3748",
                  fontFamily: "Quicksand, sans-serif",
                  lineHeight: "1.6",
                }}
              >
                {msg.message}
              </Typography>
            </Box>

            {/* Message metadata */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "16px",
                paddingTop: "12px",
                borderTop: "1px solid #E2E8F0",
              }}
            >
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "4px" }}
              >
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 500,
                    color: "#A0AEC0",
                    fontFamily: "Quicksand, sans-serif",
                    textTransform: "uppercase",
                  }}
                >
                  Cancellation Date
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#4A5568",
                    fontFamily: "Quicksand, sans-serif",
                  }}
                >
                  {dayjs(msg.cancellation_date).format(
                    "MMM DD, YYYY [at] h:mm A"
                  )}
                </Typography>
              </Box>

              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "4px" }}
              >
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 500,
                    color: "#A0AEC0",
                    fontFamily: "Quicksand, sans-serif",
                    textTransform: "uppercase",
                  }}
                >
                  Reason
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#4A5568",
                    fontFamily: "Quicksand, sans-serif",
                  }}
                >
                  {msg.reason_cancellation || "No reason provided"}
                </Typography>
              </Box>

              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "4px" }}
              >
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 500,
                    color: "#A0AEC0",
                    fontFamily: "Quicksand, sans-serif",
                    textTransform: "uppercase",
                  }}
                >
                  Message Sent
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#4A5568",
                    fontFamily: "Quicksand, sans-serif",
                  }}
                >
                  {dayjs(msg.message_time).format("MMM DD, YYYY [at] h:mm A")}
                </Typography>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    );
  }
);
